import React, { useState } from 'react'
import { useLocale, Card, Button, ShoppingItem, ImageUpload, Spinner, Badge } from '@betina/ui'
import { useQuery, useMutation } from '../hooks/useApi.js'
import { shoppingApi, stockApi, aiApi } from '../lib/api.js'

const CATEGORY_ICONS = {
  fruits: '🍎', vegetables: '🥦', meat: '🥩', fish: '🐟',
  dairy: '🧀', bakery: '🥖', frozen: '🧊', drinks: '🥤',
  snacks: '🍪', condiments: '🫙', other: '📦',
}

export function Shopping() {
  const { t } = useLocale()
  const [activeList, setActiveList] = useState(null)
  const [showNewList, setShowNewList] = useState(false)
  const [listName, setListName] = useState('')
  const [newItem, setNewItem] = useState('')
  const [showReceiptScan, setShowReceiptScan] = useState(false)
  const [receiptResult, setReceiptResult] = useState(null)
  const [scanningReceipt, setScanningReceipt] = useState(false)
  const [generatingList, setGeneratingList] = useState(false)

  const { data: lists, loading, refetch: refetchLists } = useQuery(
    () => shoppingApi.list(),
    []
  )

  const { data: currentList, loading: listLoading, refetch: refetchCurrent } = useQuery(
    () => activeList ? shoppingApi.get(activeList) : Promise.resolve(null),
    [activeList]
  )

  const { mutate: createList } = useMutation((data) => shoppingApi.create(data))
  const { mutate: addItem } = useMutation(({ listId, ...item }) => shoppingApi.addItem(listId, item))
  const { mutate: toggleItem } = useMutation(({ itemId, isChecked }) => shoppingApi.toggleItem(itemId, isChecked))
  const { mutate: bulkAddItems } = useMutation(({ listId, items }) => shoppingApi.bulkAddItems(listId, items))
  const { mutate: updateListStatus } = useMutation(({ id, status }) => shoppingApi.updateStatus(id, status))
  const { mutate: bulkStock } = useMutation((items) => stockApi.bulkCreate(items))

  async function handleCreateList() {
    if (!listName.trim()) return
    const list = await createList({ name: listName })
    setListName('')
    setShowNewList(false)
    setActiveList(list.id)
    refetchLists()
  }

  async function handleAddItem() {
    if (!newItem.trim() || !activeList) return
    await addItem({ listId: activeList, name: newItem })
    setNewItem('')
    refetchCurrent()
  }

  async function handleToggle(item) {
    await toggleItem({ itemId: item.id, isChecked: !item.is_checked })
    refetchCurrent()
  }

  async function handleReceiptScan(base64, mediaType) {
    if (!base64) { setReceiptResult(null); return }
    setScanningReceipt(true)
    try {
      const res = await aiApi.analyzeReceipt({ imageBase64: base64, mediaType })
      setReceiptResult(res.receipt)
    } finally {
      setScanningReceipt(false)
    }
  }

  async function applyReceiptToStock() {
    if (!receiptResult?.items?.length) return
    await bulkStock(
      receiptResult.items.map((i) => ({
        name: i.nameFr || i.name,
        quantity: i.quantity ?? 1,
        unit: i.unit ?? 'unit',
        location: 'fridge',
        addedVia: 'receipt',
      }))
    )
    setReceiptResult(null)
    setShowReceiptScan(false)
  }

  async function handleGenerateFromStock() {
    if (!activeList) return
    setGeneratingList(true)
    try {
      const res = await aiApi.suggestFromStock()
      if (res.suggestions?.length) {
        const missing = res.suggestions.flatMap((s) => s.missingIngredients ?? [])
        const unique = [...new Set(missing)]
        if (unique.length) {
          await bulkAddItems({ listId: activeList, items: unique.map((name) => ({ name })) })
          refetchCurrent()
        }
      }
    } finally {
      setGeneratingList(false)
    }
  }

  // Group items by category
  const itemsByCategory = (currentList?.items ?? []).reduce((acc, item) => {
    const cat = item.category ?? 'other'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(item)
    return acc
  }, {})

  const checkedCount = (currentList?.items ?? []).filter((i) => i.is_checked).length
  const totalCount = currentList?.items?.length ?? 0

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">{t('shopping')}</h1>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setShowReceiptScan(true)}>
            📷 {t('scan_receipt')}
          </Button>
          <Button size="sm" onClick={() => setShowNewList(true)}>+ Liste</Button>
        </div>
      </div>

      {/* List selector */}
      {loading ? (
        <Spinner />
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {(lists ?? []).map((list) => (
            <button
              key={list.id}
              onClick={() => setActiveList(list.id)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-sm transition-colors ${
                activeList === list.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {list.name}
            </button>
          ))}
          {!lists?.length && (
            <p className="text-sm text-gray-400">Aucune liste — créez-en une !</p>
          )}
        </div>
      )}

      {/* Active list content */}
      {activeList && (
        <>
          {/* Progress bar */}
          {totalCount > 0 && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{checkedCount}/{totalCount} articles</span>
                <span>{Math.round((checkedCount / totalCount) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-emerald-500 rounded-full h-2 transition-all"
                  style={{ width: `${(checkedCount / totalCount) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* AI & actions */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="flex-1 justify-center"
              loading={generatingList}
              onClick={handleGenerateFromStock}
            >
              ✨ Depuis le stock
            </Button>
            {checkedCount === totalCount && totalCount > 0 && (
              <Button
                size="sm"
                variant="secondary"
                className="flex-1 justify-center"
                onClick={() => updateListStatus({ id: activeList, status: 'completed' })}
              >
                ✅ Terminer
              </Button>
            )}
          </div>

          {/* Add item */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="Ajouter un article…"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
            />
            <Button onClick={handleAddItem}>{t('add')}</Button>
          </div>

          {/* Items by category */}
          {listLoading ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : totalCount === 0 ? (
            <Card className="text-center py-6">
              <p className="text-gray-400 text-sm">Liste vide — ajoutez des articles !</p>
            </Card>
          ) : (
            Object.entries(itemsByCategory).map(([category, catItems]) => (
              <div key={category}>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                  {CATEGORY_ICONS[category] ?? '📦'} {category}
                </h3>
                <Card>
                  {catItems.map((item) => (
                    <ShoppingItem
                      key={item.id}
                      item={item}
                      onToggle={handleToggle}
                    />
                  ))}
                </Card>
              </div>
            ))
          )}
        </>
      )}

      {/* New list modal */}
      {showNewList && (
        <div className="fixed inset-0 bg-black/40 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-4 space-y-3">
            <p className="font-semibold text-gray-800">Nouvelle liste de courses</p>
            <input
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="Nom de la liste…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateList()}
            />
            <div className="flex gap-2">
              <Button className="flex-1 justify-center" onClick={handleCreateList}>{t('save')}</Button>
              <Button variant="secondary" className="flex-1 justify-center" onClick={() => setShowNewList(false)}>{t('cancel')}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt scan modal */}
      {showReceiptScan && (
        <div className="fixed inset-0 bg-black/40 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-4 space-y-3">
            <p className="font-semibold text-gray-800">📷 {t('scan_receipt')}</p>
            <ImageUpload onUpload={handleReceiptScan} label="Photographier le reçu" />
            {scanningReceipt && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Lecture du reçu…
              </div>
            )}
            {receiptResult && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{receiptResult.storeName ?? 'Magasin'}</span>
                  <span className="text-gray-400">{receiptResult.date}</span>
                  {receiptResult.totalAmount && (
                    <Badge color="green">€{receiptResult.totalAmount.toFixed(2)}</Badge>
                  )}
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {receiptResult.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-xs text-gray-600">
                      <span>{item.nameFr || item.name}</span>
                      {item.isLactose && <Badge color="red">Lactose</Badge>}
                      {item.price && <span className="text-gray-400">€{item.price.toFixed(2)}</span>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1 justify-center text-xs" onClick={applyReceiptToStock}>
                    Ajouter au stock
                  </Button>
                  <Button variant="secondary" className="flex-1 justify-center text-xs" onClick={() => setReceiptResult(null)}>
                    Refaire
                  </Button>
                </div>
              </div>
            )}
            <Button variant="secondary" className="w-full justify-center" onClick={() => { setShowReceiptScan(false); setReceiptResult(null) }}>
              {t('cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
